import React from "react";
import { Play, Pause, ArrowLeftRight } from "lucide-react";

const items = [
    {
        slug: "lawn-edge",
        title: "Lawn & edge tidy",
        location: "Backyard tidy · One visit",
        before: {
            poster: "/media/gallery/lawn-before.jpg",
            src: "/media/gallery/lawn-before.mp4",
        },
        after: {
            poster: "/media/gallery/lawn-after.jpg",
            src: "/media/gallery/lawn-after.mp4",
        },
    },
    {
        slug: "lawn-returfing",
        title: "Lawn returfing",
        location: "Patchy lawn → fresh turf",
        before: {
            poster: "/media/gallery/returf-before.jpg",
            src: "/media/gallery/returf-before.mp4",
        },
        after: {
            poster: "/media/gallery/returf-after.jpg",
            src: "/media/gallery/returf-after.mp4",
        },
    },
];

function ResultCard({ item, i }) {
    const [showAfter, setShowAfter] = React.useState(true);
    const [playing, setPlaying] = React.useState(false);
    const beforeRef = React.useRef(null);
    const afterRef = React.useRef(null);

    const activeRef = showAfter ? afterRef : beforeRef;
    const idleRef = showAfter ? beforeRef : afterRef;

    const togglePlay = (e) => {
        e?.stopPropagation?.();
        const v = activeRef.current;
        const o = idleRef.current;
        if (!v) return;
        if (o) {
            o.pause();
        }
        if (v.paused) {
            v.play().catch(() => {});
            setPlaying(true);
        } else {
            v.pause();
            setPlaying(false);
        }
    };

    const swap = (e) => {
        e?.stopPropagation?.();
        const cur = activeRef.current;
        if (cur) {
            cur.pause();
            cur.currentTime = 0;
        }
        setPlaying(false);
        setShowAfter((s) => !s);
    };

    React.useEffect(() => {
        const onVisChange = () => {
            [beforeRef.current, afterRef.current].forEach((v) => v && v.pause());
            setPlaying(false);
        };
        document.addEventListener("visibilitychange", onVisChange);
        return () => document.removeEventListener("visibilitychange", onVisChange);
    }, []);

    return (
        <article
            data-testid={`gallery-card-${item.slug}`}
            className="reveal group rounded-2xl overflow-hidden border border-line bg-[var(--paper)] flex flex-col"
            style={{ transitionDelay: `${i * 100}ms` }}
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-[var(--ink)]">
                {/* Before video layer */}
                <video
                    ref={beforeRef}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        showAfter ? "opacity-0" : "opacity-100"
                    }`}
                    src={item.before.src}
                    poster={item.before.poster}
                    muted
                    playsInline
                    preload="none"
                    loop
                    onEnded={() => setPlaying(false)}
                    data-testid={`gallery-before-${item.slug}`}
                />
                {/* After video layer */}
                <video
                    ref={afterRef}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                        showAfter ? "opacity-100" : "opacity-0"
                    }`}
                    src={item.after.src}
                    poster={item.after.poster}
                    muted
                    playsInline
                    preload="none"
                    loop
                    onEnded={() => setPlaying(false)}
                    data-testid={`gallery-after-${item.slug}`}
                />

                {/* Subtle gradient for label legibility */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-transparent to-black/30 pointer-events-none" />

                {/* State label */}
                <span
                    className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[rgba(242,237,227,0.92)] text-[var(--ink)] text-[11px] font-mono-x uppercase tracking-widest"
                    data-testid={`gallery-state-${item.slug}`}
                >
                    {showAfter ? "After" : "Before"}
                </span>

                {/* Play / Pause overlay button */}
                <button
                    type="button"
                    onClick={togglePlay}
                    aria-label={playing ? "Pause clip" : "Play clip"}
                    data-testid={`gallery-play-${item.slug}`}
                    className="absolute inset-0 flex items-center justify-center group/play"
                >
                    <span
                        className={`w-16 h-16 rounded-full bg-[var(--paper)]/90 backdrop-blur-md flex items-center justify-center text-[var(--ink)] shadow-lg transition-all duration-300 ${
                            playing
                                ? "opacity-0 scale-90"
                                : "opacity-100 scale-100 group-hover/play:scale-110"
                        }`}
                    >
                        {playing ? (
                            <Pause size={20} fill="currentColor" />
                        ) : (
                            <Play size={20} fill="currentColor" className="translate-x-[2px]" />
                        )}
                    </span>
                </button>

                {/* Swap button */}
                <button
                    type="button"
                    onClick={swap}
                    aria-label="Switch before / after"
                    data-testid={`gallery-toggle-${item.slug}`}
                    className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[var(--ink)]/85 text-[var(--paper)] text-[11px] font-medium backdrop-blur-md hover:bg-[var(--terra)] transition-colors"
                >
                    <ArrowLeftRight size={13} />
                    Swap
                </button>
            </div>
            <div className="p-6 flex items-end justify-between gap-4">
                <div>
                    <h3 className="font-display text-xl tracking-tight">
                        {item.title}
                    </h3>
                    <div className="font-mono-x text-[11px] text-[rgba(14,14,16,0.58)] mt-1">
                        {item.location}
                    </div>
                </div>
                <span className="font-mono-x text-[11px] text-[rgba(14,14,16,0.38)]">
                    #{String(i + 1).padStart(2, "0")}
                </span>
            </div>
        </article>
    );
}

export default function Gallery() {
    return (
        <section
            id="gallery"
            data-testid="section-gallery"
            className="relative bg-[var(--paper-2)] py-28 md:py-40 border-t border-line"
        >
            <div className="maxw section-pad">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
                    <div className="md:col-span-7 reveal">
                        <div className="eyebrow mb-5">Recent results</div>
                        <h2 className="font-display font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                            Before &amp;{" "}
                            <span className="italic text-[var(--terra)]">after</span>{" "}
                            — tap to play.
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <p className="text-[rgba(14,14,16,0.68)] text-[14.5px] leading-relaxed max-w-md md:ml-auto">
                            Real clips from real jobs. Hit play, then swap
                            between the before and after to see the change.
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl">
                    {items.map((it, i) => (
                        <ResultCard key={it.slug} item={it} i={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
